import React, { useState } from 'react';
import { 
  FaShieldAlt,
  FaLock,
  FaUserShield,
  FaFileContract,
  FaClipboardCheck,
  FaExclamationTriangle,
  FaCheckCircle,
  FaBook,
  FaBalanceScale,
  FaHandshake,
  FaInfoCircle,
  FaArrowRight,
  FaPhone,
  FaWhatsapp,
  FaEnvelope
} from 'react-icons/fa';
import '../style.css';

const PolicyPage = () => {
  const [activeSection, setActiveSection] = useState('privacy');

  const policySections = [
    {
      id: 'privacy',
      title: 'Privacy Policy',
      icon: <FaLock />,
      color: '#2ecc71'
    },
    {
      id: 'terms',
      title: 'Terms & Conditions',
      icon: <FaFileContract />,
      color: '#3498db'
    },
    {
      id: 'cancellation',
      title: 'Cancellation Policy',
      icon: <FaClipboardCheck />,
      color: '#e74c3c'
    },
    {
      id: 'safety',
      title: 'Safety Guidelines',
      icon: <FaUserShield />,
      color: '#f39c12'
    }
  ];

  const privacyPolicyContent = [
    {
      title: 'Information We Collect',
      content: `We collect personal information that you voluntarily provide when you book our taxi services, including:
      • Name, contact details (phone, email)
      • Pickup and drop locations
      • Travel dates and times
      • Payment information (securely processed)
      • Vehicle preferences`,
      icon: <FaUserShield />
    },
    {
      title: 'How We Use Your Information',
      content: `Your information is used to:
      • Process bookings and provide taxi services
      • Communicate booking confirmations and updates
      • Improve our services and customer experience
      • Send important updates and offers (with your consent)
      • Comply with legal requirements`,
      icon: <FaShieldAlt />
    },
    {
      title: 'Data Protection',
      content: `We implement robust security measures:
      • Secure SSL encryption for all transactions
      • Restricted access to personal data
      • Regular security audits
      • Compliance with Indian data protection laws
      • Data retention only as required by law`,
      icon: <FaLock />
    },
    {
      title: 'Third-Party Sharing',
      content: `We do not sell your personal information. Limited sharing occurs only with:
      • Service providers (for booking processing)
      • Legal authorities when required
      • Payment gateways for transaction processing
      • Insurance companies for claims (if applicable)`,
      icon: <FaHandshake />
    }
  ];

  const termsContent = [
    {
      title: 'Booking Terms',
      content: `• All bookings are subject to vehicle availability
      • Advance booking recommended (especially for peak seasons)
      • Minimum 2 hours notice required for airport/railway pickups
      • Same-day bookings subject to availability`,
      icon: <FaBook />
    },
    {
      title: 'Payment Terms',
      content: `• 50% advance required for bookings above ₹5000
      • Full payment for same-day bookings
      • Accepted payment methods: Cash, UPI, Credit/Debit Cards
      • GST @ 5% applicable on all services
      • Receipt provided for all payments`,
      icon: <FaBalanceScale />
    },
    {
      title: 'Service Terms',
      content: `• Driver will wait 30 minutes at pickup location
      • Additional waiting charges: ₹100 per 30 minutes after grace period
      • Route deviations may incur extra charges
      • AC usage included in quoted price
      • Overnight charges applicable for outstation trips`,
      icon: <FaHandshake />
    },
    {
      title: 'Customer Responsibilities',
      content: `• Provide accurate pickup/drop details
      • Be ready at scheduled pickup time
      • Inform about changes well in advance
      • Treat vehicle and driver with respect
      • Report any issues immediately`,
      icon: <FaInfoCircle />
    }
  ];

  const cancellationContent = [
    {
      title: 'Cancellation Charges',
      content: `• Before 24 hours: Full refund (minus ₹100 processing fee)
      • 12-24 hours before: 50% refund
      • Less than 12 hours: No refund
      • No-show: Full booking amount forfeited
      • Natural calamities: Full refund`,
      icon: <FaExclamationTriangle />
    },
    {
      title: 'Refund Process',
      content: `• Refunds processed within 7 working days
      • Same payment method used for refund
      • Refund status can be tracked via booking ID
      • Contact customer care for refund queries
      • GST amount non-refundable`,
      icon: <FaCheckCircle />
    },
    {
      title: 'Modification Policy',
      content: `• Date/time changes: Free if made 6+ hours in advance
      • Vehicle upgrade: Pay difference amount
      • Vehicle downgrade: No refund for difference
      • Route changes: Charges may apply
      • Contact for any modifications`,
      icon: <FaClipboardCheck />
    }
  ];

  const safetyContent = [
    {
      title: 'Vehicle Safety',
      content: `• All vehicles regularly serviced and maintained
      • Comprehensive insurance coverage
      • GPS tracking in all vehicles
      • Emergency toolkit and first aid available
      • Daily sanitization and cleaning`,
      icon: <FaShieldAlt />
    },
    {
      title: 'Driver Standards',
      content: `• Verified background checks
      • Professional driving license
      • Tourist guide certification (where required)
      • First aid training
      • Local route knowledge`,
      icon: <FaUserShield />
    },
    {
      title: 'COVID-19 Safety',
      content: `• Masks compulsory for drivers
      • Hand sanitizers available
      • Temperature checks (if required)
      • Contactless payment preferred
      • Regular vehicle disinfection`,
      icon: <FaLock />
    }
  ];

  const getActiveContent = () => {
    switch(activeSection) {
      case 'privacy': return privacyPolicyContent;
      case 'terms': return termsContent;
      case 'cancellation': return cancellationContent;
      case 'safety': return safetyContent;
      default: return privacyPolicyContent;
    }
  };

  return (
    <div className="policy-container">
      {/* Header */}
      <div className="policy-header">
        <div className="policy-header-content">
          <h1>Policies & Terms</h1>
          <p>Shree Balaji Travels - Varanasi</p>
          <div className="policy-badges">
            <span className="policy-badge">
              <FaShieldAlt /> Secure & Transparent
            </span>
            <span className="policy-badge">
              <FaHandshake /> Customer First
            </span>
            <span className="policy-badge">
              <FaBalanceScale /> Fair Practices
            </span>
          </div>
        </div>
      </div>

      <div className="policy-main">
        {/* Side Navigation */}
        <div className="policy-sidebar">
          <div className="policy-nav">
            <h3>Our Policies</h3>
            {policySections.map((section) => (
              <button
                key={section.id}
                className={`policy-nav-btn ${activeSection === section.id ? 'active' : ''}`}
                onClick={() => setActiveSection(section.id)}
                style={{ '--btn-color': section.color }}
              >
                <span className="policy-nav-icon" style={{ color: section.color }}>
                  {section.icon}
                </span>
                {section.title}
                {activeSection === section.id && <FaArrowRight className="nav-arrow" />}
              </button>
            ))}
          </div>

          {/* Contact Box */}
          <div className="policy-contact-box">
            <h4>Need Help?</h4>
            <p>Contact us for policy queries</p>
            <div className="contact-methods">
              <a href="tel:+919415799879" className="contact-link">
                <FaPhone /> +91 9415799879
              </a>
              <a href="https://wa.me/919415799879" className="contact-link">
                <FaWhatsapp /> WhatsApp
              </a>
              <a href="mailto:info@shreebalajitravels.com" className="contact-link">
                <FaEnvelope /> Email Us
              </a>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="policy-content">
          <div className="content-header">
            <div className="content-title">
              {policySections.find(s => s.id === activeSection)?.icon}
              <h2>{policySections.find(s => s.id === activeSection)?.title}</h2>
            </div>
            <p className="content-subtitle">
              Last Updated: {new Date().toLocaleDateString('en-IN', { 
                day: 'numeric', 
                month: 'long', 
                year: 'numeric' 
              })}
            </p>
          </div>

          {/* Policy Cards */}
          <div className="policy-cards-container">
            {getActiveContent().map((item, index) => (
              <div key={index} className="policy-card">
                <div className="policy-card-header">
                  <div className="policy-card-icon">
                    {item.icon}
                  </div>
                  <h3>{item.title}</h3>
                </div>
                <div className="policy-card-content">
                  {item.content.split('\n').map((line, i) => (
                    <p key={i} className="policy-line">
                      {line.trim().startsWith('•') ? (
                        <>
                          <span className="bullet">•</span>
                          {line.substring(1).trim()}
                        </>
                      ) : (
                        line
                      )}
                    </p>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Additional Info */}
          <div className="policy-notice">
            <div className="notice-icon">
              <FaInfoCircle />
            </div>
            <div className="notice-content">
              <h4>Important Notice</h4>
              <p>
                These policies are subject to change without prior notice. For the most current 
                version, please contact our customer service or visit our office. By using our 
                services, you agree to abide by these policies.
              </p>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="policy-actions">
            <button className="action-btn print-btn">
              <FaFileContract /> Print Policy
            </button>
            <button className="action-btn download-btn">
              <FaClipboardCheck /> Download PDF
            </button>
            <button className="action-btn contact-btn">
              <FaPhone /> Contact Support
            </button>
          </div>
        </div>
      </div>

      
    </div>
  );
};

export default PolicyPage;